import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useCopyToClipboard } from 'app/frontend/base/hooks/useCopyToClipboard'
import { gameVersionToSubdomain } from 'app/frontend/types/custom/GameVersion'
import { useTranslation } from 'react-i18next'
import SpreadsheetGetStarted from './SpreadsheetGetStarted'
import SpreadsheetExisting from './SpreadsheetExisting'
import Card from 'app/frontend/base/components/Card'

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
}

const SpreadsheetDialog = ({ open, setOpen }: Props) => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { accessLevel } = useAuth()
  const { copied, copyToClipboard } = useCopyToClipboard()

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className='max-w-3xl'
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Spreadsheet</DialogTitle>
        </DialogHeader>

        <div>
          As an alternative to this page, insights into your team&apos;s
          characters are exposed in an extensive spreadsheet. Once set up, data
          in the spreadsheet will be updated automatically. To get a new
          spreadsheet, make a{' '}
          <a
            href={`https://${gameVersionToSubdomain[gameVersion]}wowaudit.com/copy`}
            className='text-primary hover:text-primary/90'
            target='_blank'
            rel='noreferrer'
          >
            copy
          </a>{' '}
          and then{' '}
          {accessLevel === 'admin' ? (
            <span>
              paste the key
              <Tooltip>
                <TooltipTrigger
                  onClick={(e) => {
                    e.preventDefault()
                    copyToClipboard(team.readonlyKey ?? '')
                  }}
                >
                  <code className='text-xs bg-muted rounded-md px-1 py-0.5 mx-1'>
                    {team.readonlyKey}
                  </code>
                </TooltipTrigger>
                <TooltipContent>
                  {copied
                    ? 'Copied!'
                    : t('dashboard.team.settings.click-to-copy')}
                </TooltipContent>
              </Tooltip>
              in the settings tab of your new spreadsheet.
            </span>
          ) : (
            <span>
              ask one of your team&apos;s admins to provide you with the
              spreadsheet key (found in the team settings).
            </span>
          )}
        </div>

        <Card>
          {team.spreadsheetPath ? (
            <SpreadsheetExisting />
          ) : (
            <SpreadsheetGetStarted />
          )}
        </Card>
      </DialogContent>
    </Dialog>
  )
}

export default SpreadsheetDialog
